/**
 * SanPlumb — Exportación de BOM a XLSX con estilos (§9 del spec)
 *
 * Dos hojas:
 *   - "BOM": metrado completo con header oscuro, AF en azul claro, AC en
 *     rojo claro, sanitarios en gris neutro.
 *   - "Resumen": totales por sistema + factor de desperdicio aplicado.
 *
 * El armado de las filas vive en el engine (`engine/bom.ts`) — testeable
 * sin SheetJS. Este módulo solo aplica los estilos en celda usando
 * `xlsx-js-style` (fork community de SheetJS con soporte de `cell.s`).
 *
 * NOTA: `xlsx` community ignora estilos al escribir; `xlsx-js-style` es
 * un drop-in replacement de la misma API (`utils.aoa_to_sheet`,
 * `utils.book_new`, `writeFile`) que SÍ persiste los estilos.
 */

// `xlsx-js-style` se importa de forma DINÁMICA dentro de exportBOMtoXLSX()
// para que no cuente en el bundle inicial (~350 KB). Solo se descarga al
// pulsar "Exportar BOM" la primera vez en la sesión.
import type { WorkSheet } from 'xlsx-js-style';
import type { SystemData } from '../types';
import type { BrandState } from '../types/ui';
import { buildBOM, bomToXLSXRows, buildResumenXLSXRows } from '../engine/bom';

// ──────────────────────────────────────────────────────────────────
// Estilos
// ──────────────────────────────────────────────────────────────────

interface CellStyle {
  font?: { name?: string; sz?: number; bold?: boolean; color?: { rgb: string } };
  fill?: { fgColor: { rgb: string } };
  alignment?: { horizontal?: 'left' | 'center' | 'right'; vertical?: 'center' };
  border?: {
    top?: { style: string; color: { rgb: string } };
    bottom?: { style: string; color: { rgb: string } };
    left?: { style: string; color: { rgb: string } };
    right?: { style: string; color: { rgb: string } };
  };
}

const HEADER_STYLE: CellStyle = {
  font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
  fill: { fgColor: { rgb: 'FF0B1422' } }, // var(--panel) §XIII
  alignment: { horizontal: 'left', vertical: 'center' },
};
const ROW_AF: CellStyle = {
  font: { name: 'Calibri', sz: 10, color: { rgb: 'FF052238' } },
  fill: { fgColor: { rgb: 'FFE0F4FF' } }, // AF muy claro
};
const ROW_AC: CellStyle = {
  font: { name: 'Calibri', sz: 10, color: { rgb: 'FF3A0808' } },
  fill: { fgColor: { rgb: 'FFFFE0E0' } }, // AC muy claro
};
const ROW_NEUTRAL: CellStyle = {
  font: { name: 'Calibri', sz: 10, color: { rgb: 'FF1A2A40' } },
  fill: { fgColor: { rgb: 'FFF5F5F5' } },
};
const RESUMEN_TITLE: CellStyle = {
  font: { name: 'Calibri', sz: 12, bold: true, color: { rgb: 'FFFFFFFF' } },
  fill: { fgColor: { rgb: 'FF0B1422' } },
};
const RESUMEN_AF: CellStyle = { ...ROW_AF, font: { ...ROW_AF.font, bold: true } };
const RESUMEN_AC: CellStyle = { ...ROW_AC, font: { ...ROW_AC.font, bold: true } };

// Anchos de columna para BOM (6 columnas)
const BOM_COL_WIDTHS = [{ wch: 60 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 18 }];
const RESUMEN_COL_WIDTHS = [{ wch: 45 }, { wch: 25 }];

// ──────────────────────────────────────────────────────────────────
// Helpers para aplicar estilo a una fila completa
// ──────────────────────────────────────────────────────────────────

function colLetter(idx: number): string {
  // 0 → A, 1 → B, …, 25 → Z, 26 → AA …
  let out = '';
  let n = idx;
  while (n >= 0) {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  }
  return out;
}

function styleRow(
  ws: WorkSheet,
  rowIdx: number,
  numCols: number,
  style: CellStyle,
): void {
  for (let c = 0; c < numCols; c++) {
    const addr = `${colLetter(c)}${rowIdx + 1}`;
    if (!ws[addr]) ws[addr] = { v: '', t: 's' };
    (ws[addr] as { s?: CellStyle }).s = style;
  }
}

/** Devuelve el estilo para una fila según el sistema marcado en la 2ª columna. */
function styleForRow(systemCell: unknown): CellStyle {
  if (systemCell === 'AF') return ROW_AF;
  if (systemCell === 'AC') return ROW_AC;
  return ROW_NEUTRAL;
}

// ──────────────────────────────────────────────────────────────────
// API pública
// ──────────────────────────────────────────────────────────────────

/**
 * Genera y descarga el archivo XLSX del BOM con estilos por sistema.
 * Carga `xlsx-js-style` dinámicamente (no engorda el bundle inicial).
 *
 * @param system    Sistema sanitario activo.
 * @param filename  Nombre del archivo (default: sanplumb-bom.xlsx).
 */
export async function exportBOMtoXLSX(
  system: SystemData,
  filename = 'sanplumb-bom.xlsx',
  brands?: BrandState,
): Promise<void> {
  const XLSX = (await import('xlsx-js-style')).default;
  const wb = XLSX.utils.book_new();

  // ── Hoja BOM ─────────────────────────────────────────────────────
  const bomRows = bomToXLSXRows(buildBOM(system), brands);
  const wsBom = XLSX.utils.aoa_to_sheet(bomRows as unknown[][]);
  wsBom['!cols'] = BOM_COL_WIDTHS;

  const numCols = bomRows[0]?.length ?? 6;
  // Header (fila 0)
  styleRow(wsBom, 0, numCols, HEADER_STYLE);
  // Body (filas 1..n)
  for (let r = 1; r < bomRows.length; r++) {
    const sysCell = bomRows[r][1]; // 2ª columna = Sistema
    styleRow(wsBom, r, numCols, styleForRow(sysCell));
  }
  XLSX.utils.book_append_sheet(wb, wsBom, 'BOM');

  // ── Hoja Resumen ────────────────────────────────────────────────
  const resumenRows = buildResumenXLSXRows(system);
  const wsResumen = XLSX.utils.aoa_to_sheet(resumenRows as unknown[][]);
  wsResumen['!cols'] = RESUMEN_COL_WIDTHS;

  // Tinte por fila según contenido (etiquetas conocidas)
  for (let r = 0; r < resumenRows.length; r++) {
    const label = String(resumenRows[r][0] ?? '');
    let style: CellStyle = ROW_NEUTRAL;
    if (label === 'RESUMEN' || label === 'Sistema' || label === 'Notas') style = RESUMEN_TITLE;
    else if (label.startsWith('Agua Fría')) style = RESUMEN_AF;
    else if (label.startsWith('Agua Caliente')) style = RESUMEN_AC;
    styleRow(wsResumen, r, 2, style);
  }
  XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

  XLSX.writeFile(wb, filename);
}
